import React, { useEffect, useState } from "react";
import s from "~/components/Forms/CreateDocument/CreateDocument.less";
import { useForm } from "react-hook-form/dist/index.ie11";
import { Input } from "~/shared/ui/Fields/Input";
import Select from "~/shared/ui/Fields/Select";
import Button from "~/shared/ui/Fields/Button";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import { cameraAddValidation } from "~/components/Forms/CameraForm/validation";
import { useSelectorDataConfigurator } from "~/hooks/selectorDataConfigurator";
import {
  Camera,
  CameraZone,
  useGetCameraZoneByIdLazyQuery,
  useGetCameraZonesLazyQuery,
} from "~/schemas/generated/graphql";
import { useParams } from "react-router-dom";
import Preloader from "~/components/Preloader";
import { get } from "lodash";
import { TIdentifier, TSelectItem } from "~/model/common";

const defaultValues = (selectedZone?: CameraZone) => ({
  value: selectedZone?.id || undefined,
  label: selectedZone?.name || "Не выбрано",
});

export type TCameraFormType = {
  name: string;
  cameraUrl: string;
  cameraZoneId: TSelectItem<TIdentifier>;
};

interface TCameraFormProps {
  submitHandler: (data: TCameraFormType) => void;
  cameraInstance?: Camera;
}
const CameraForm = ({ submitHandler, cameraInstance }: TCameraFormProps) => {
  // Utils
  const { t } = useTranslation();
  const params = useParams<{ id: string }>();
  const { id } = params;

  const zonesSelectInstance = useSelectorDataConfigurator({
    queryGenerator: useGetCameraZonesLazyQuery,
    responseDataGetterString: "CameraGetter.getCameraZones.items",
    searchConfig: (searchText) => ({ name: searchText }),
  });

  // Form
  const { handleSubmit, control, errors, setValue, reset } =
    useForm<TCameraFormType>({
      resolver: yupResolver(cameraAddValidation(t)),
    });

  // State
  const [selectedZone, setSelectedZone] = useState<CameraZone | undefined>();

  // Queries
  const [getZone, getZoneState] = useGetCameraZoneByIdLazyQuery({
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (id) {
      getZone({ variables: { id } });
    }
  }, []);

  useEffect(() => {
    if (!getZoneState.loading && getZoneState.data) {
      setSelectedZone(get(getZoneState.data, "CameraGetter.getCameraZone"));
    }
  }, [getZoneState.loading]);

  useEffect(() => {
    if (selectedZone) setValue("cameraZoneId", defaultValues(selectedZone));
  }, [selectedZone]);

  useEffect(() => {
    if (cameraInstance) {
      const { name, cameraUrl } = cameraInstance;
      reset({ name, cameraUrl, cameraZoneId: defaultValues(selectedZone) });
    }
  }, [cameraInstance]);

  if (getZoneState.loading) return <Preloader />;

  return (
    <form className={s.wrapper} onSubmit={handleSubmit(submitHandler)}>
      <div className={s.container}>
        <Input
          label={"Название"}
          name={"name"}
          control={control}
          error={errors["name"]?.message}
        />
        <Input
          label={"RTSP ссылка"}
          name={"cameraUrl"}
          control={control}
          error={errors["cameraUrl"]?.message}
        />
        <Select
          label={"Зона просмотра"}
          name={"cameraZoneId"}
          control={control}
          optionObjects={zonesSelectInstance.rowsForSelect}
          onScrollBottom={zonesSelectInstance.introspectionHandler}
          getSearchText={zonesSelectInstance.searchValue}
          customClassName={s.select}
          error={errors["cameraZoneId"]?.value.message}
          menuListHeight={"100px"}
        />
      </div>
      <Button txtBtn={t("ui.save")} isForm />
    </form>
  );
};

export default CameraForm;
